import React from 'react';
import { useTemplateFlowManager } from './Hooks/UseTemplateFlowManager';
import { Box, Alert } from '@mui/material';
import { ThemeContext } from 'styled-components';
import { useContext } from 'react';
import WithActions from "../../common/components/Wrappers/WithActions";
import TemplateInfoStep from './Steps/TemplateInfoStep';
import ParticipantsStep from './Steps/ParticipantsStep';
import InitialStatementStep from './Steps/InitialStatementStep';
import StepIndicator from './StepIndicator';

/**
 * TemplateFlowManager - Component for managing template creation flow
 */
const TemplateFlowManager = ({
    onStepTransition: customOnStepTransition,
    initialStepConfig: customInitialStepConfig
}) => {
    const theme = useContext(ThemeContext);

    const flowData = useTemplateFlowManager({
        onStepTransition: customOnStepTransition || ((step, error) => {
            // Default step transition handling
        }),
        initialStepConfig: customInitialStepConfig || {
            templateInfo: 'enabled',
            participants: 'hidden',
            initialStatement: 'hidden'
        }
    });

    const renderStep = (stepName, key) => {
        const stepEnabled = flowData.isStepEnabled(stepName);
        const stepDisabled = flowData.isStepDisabled(stepName);
        const isCurrentStep = flowData.currentStep === stepName;
        const stepData = flowData.getStepData(stepName);

        if (!stepData) {
            return null;
        }

        const commonProps = {
            disabled: stepDisabled,
            isLoading: stepEnabled ? flowData.isLoading : false,
            isCompleted: stepDisabled && !stepEnabled,
            isVisible: flowData.isStepVisible(stepName),
            isActive: isCurrentStep
        };

        switch (stepName) {
            case 'templateInfo':
                return (
                    <TemplateInfoStep
                        key={key}
                        formData={stepData.formData}
                        onChange={stepData.onChange}
                        onSubmit={stepData.onSubmit}
                        {...commonProps}
                    />
                );
            case 'participants':
                return (
                    <ParticipantsStep
                        key={key}
                        templateId={stepData.templateId}
                        participants={stepData.participants}
                        onAddParticipant={stepData.onAddParticipant}
                        onRemoveParticipant={stepData.onRemoveParticipant}
                        onContinue={stepData.onContinue}
                        groupId={stepData.groupId}
                        {...commonProps}
                    />
                );
            case 'initialStatement':
                return (
                    <InitialStatementStep
                        key={key}
                        formData={stepData.formData}
                        onChange={stepData.onChange}
                        onSubmit={stepData.onSubmit}
                        onSkip={stepData.onSkip}
                        {...commonProps}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                margin: 'auto',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'visible'
            }}
        >
            {/* Step Indicator - sticky so it stays visible when scrolling */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    flexShrink: 0,
                    backgroundColor: theme.colors.backgroundPrimary,
                    marginLeft: -3,
                    marginRight: -3,
                    marginTop: 0,
                    paddingLeft: 3,
                    paddingRight: 3,
                    paddingTop: 1,
                }}
            >
                <StepIndicator currentStep={flowData.currentStep} stepConfig={flowData.stepConfig} />
            </Box>

            {/* Render only the current step so the steps header hugs the top and layout matches Participants step (one scrollable body + sticky footer) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {renderStep(flowData.currentStep, flowData.currentStep)}
            </Box>

            {/* Validation Error Display */}
            {flowData.error && (
                <WithActions forceSpacing={true}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Alert severity="error" sx={{ m: 0, width: '100%' }}>
                            {flowData.error}
                        </Alert>
                    </Box>
                </WithActions>
            )}
        </Box>
    );
};

export default TemplateFlowManager;
